/**
 * FlashcardWaveformPlayer Component
 *
 * Visual waveform player for flashcard audio with precise timestamp controls.
 * Features loop playback, confidence-based region coloring, and keyboard shortcuts.
 *
 * Terminal theme styling with cyan/pink/green colors.
 */

import { useState, useCallback } from 'react'
import {
  useWaveSurferPlayer,
  type FlashcardWithAudio,
} from '@/hooks/useWaveSurferPlayer'
import { Play, Pause, SkipBack, SkipForward, Repeat, Repeat1, X } from 'lucide-react'
import { useAction } from 'convex/react'
import { api } from 'convex/_generated/api'
import type { Id } from 'convex/_generated/dataModel'

type AnkiGrade = 0 | 1 | 2 | 3 | 4 | 5

interface FlashcardWaveformPlayerProps {
  audioUrl: string
  flashcards: FlashcardWithAudio[]
  initialIndex?: number
  onIndexChange?: (newIndex: number) => void
  onClose?: () => void
}

export function FlashcardWaveformPlayer({
  audioUrl,
  flashcards,
  initialIndex = 0,
  onIndexChange,
  onClose,
}: FlashcardWaveformPlayerProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)

  const handleIndexChange = (newIndex: number) => {
    setCurrentIndex(newIndex)
    onIndexChange?.(newIndex)
  }

  const submitReview = useAction(api.flashcards.submitFlashcardReview)

  const handleGrade = useCallback(
    async (grade: AnkiGrade) => {
      const currentFlashcard = flashcards[currentIndex]
      if (!currentFlashcard) return

      try {
        await submitReview({
          flashcardId: currentFlashcard._id as Id<'flashcards'>,
          grade,
        })

        // Move to next flashcard after grading
        const nextIndex = (currentIndex + 1) % flashcards.length
        handleIndexChange(nextIndex)
      } catch (error) {
        console.error('Failed to submit review:', error)
        alert('Failed to submit review. Please try again.')
      }
    },
    [currentIndex, flashcards, submitReview, handleIndexChange]
  )

  const {
    waveformRef,
    isReady,
    isPlaying,
    currentTime,
    duration,
    loopMode,
    hasUserInteraction,
    play,
    togglePlayPause,
    playRegion,
    goToPrevious,
    goToNext,
    cycleLoopMode,
  } = useWaveSurferPlayer({
    audioUrl,
    flashcards,
    currentIndex,
    onIndexChange: handleIndexChange,
  })

  const currentFlashcard = flashcards[currentIndex]

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="terminal-border p-4 mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-terminal-green font-mono mb-1">
              [FLASHCARD AUDIO PLAYER]
            </h1>
            <p className="text-trail-tan font-mono text-sm">
              Card {currentIndex + 1} of {flashcards.length}
            </p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-trail-tan hover:text-white transition-colors"
              title="Close player"
            >
              <X size={24} />
            </button>
          )}
        </div>

        {/* Flashcard Content */}
        <div className="terminal-border p-6 mb-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Spanish */}
            <div>
              <div className="text-xs text-trail-tan font-mono mb-2 uppercase">
                Spanish ({currentFlashcard.frontSpanish.length} lines)
              </div>
              <div className="text-white font-mono text-lg space-y-1">
                {currentFlashcard.frontSpanish.map((line: string, idx: number) => (
                  <div key={idx}>{line}</div>
                ))}
              </div>
            </div>

            {/* English */}
            <div>
              <div className="text-xs text-trail-tan font-mono mb-2 uppercase">English</div>
              <div className="text-terminal-green font-mono text-lg space-y-1">
                {currentFlashcard.backEnglish.map((line: string, idx: number) => (
                  <div key={idx}>{line}</div>
                ))}
              </div>
            </div>
          </div>

          {/* Audio Clip Info */}
          {currentFlashcard.audioClip && (
            <div className="mt-6 pt-6 border-t-2 border-gray-800">
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-xs text-trail-tan font-mono mb-1">START TIME</div>
                  <div className="text-sky-blue font-mono">
                    {currentFlashcard.audioClip.startTime.toFixed(2)}s
                  </div>
                </div>
                <div>
                  <div className="text-xs text-trail-tan font-mono mb-1">END TIME</div>
                  <div className="text-sky-blue font-mono">
                    {currentFlashcard.audioClip.endTime.toFixed(2)}s
                  </div>
                </div>
                <div>
                  <div className="text-xs text-trail-tan font-mono mb-1">DURATION</div>
                  <div className="text-sky-blue font-mono">
                    {currentFlashcard.audioClip.duration.toFixed(2)}s
                  </div>
                </div>
                <div>
                  <div className="text-xs text-trail-tan font-mono mb-1">CONFIDENCE</div>
                  <div
                    className={`font-mono ${
                      currentFlashcard.audioClip.alignmentConfidence >= 0.85
                        ? 'text-terminal-green'
                        : currentFlashcard.audioClip.alignmentConfidence >= 0.70
                        ? 'text-yellow-500'
                        : 'text-red-500'
                    }`}
                  >
                    {(currentFlashcard.audioClip.alignmentConfidence * 100).toFixed(0)}%
                  </div>
                </div>
              </div>
              {currentFlashcard.audioClip.needsReview && (
                <div className="mt-4 text-yellow-500 font-mono text-sm">
                  ⚠ This clip has been flagged for manual review
                </div>
              )}
            </div>
          )}
        </div>

        {/* Grading UI - Reused from /app/flashcards.tsx */}
        <div className="terminal-border p-6 mb-6">
          <div className="text-sm text-trail-tan font-mono mb-4 uppercase text-center">
            How well did you know this?
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-4 sm:gap-8">
            {[
              { grade: 0 as AnkiGrade, label: '0', desc: 'Forgot', color: 'bg-red-600' },
              { grade: 1 as AnkiGrade, label: '1', desc: 'Hard', color: 'bg-orange-400' },
              { grade: 2 as AnkiGrade, label: '2', desc: 'OK', color: 'bg-yellow-600' },
              { grade: 3 as AnkiGrade, label: '3', desc: 'Good', color: 'bg-blue-600' },
              { grade: 4 as AnkiGrade, label: '4', desc: 'Easy', color: 'bg-teal-600' },
              { grade: 5 as AnkiGrade, label: '5', desc: 'Perfect', color: 'bg-green-600' },
            ].map(({ grade, label, desc, color }) => (
              <button
                key={grade}
                onClick={() => handleGrade(grade)}
                className={`${color} text-white font-bold font-mono text-lg sm:text-xl py-3 sm:py-5 border-4 border-dotted border-white rounded-lg
                  hover:border-terminal-green cursor-pointer
                  transition-all min-h-[44px] sm:min-h-[60px]`}
              >
                <div className="text-2xl sm:text-3xl mb-1">{label}</div>
                <div className="text-xs sm:text-sm">{desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Waveform Visualization */}
        <div className="terminal-border p-6 mb-6">
          <div className="mb-4">
            <div className="text-xs text-trail-tan font-mono mb-2 uppercase">Waveform</div>
            {!isReady && (
              <div className="text-sky-blue font-mono text-sm mb-4">Loading audio...</div>
            )}
          </div>

          {/* Waveform container */}
          <div ref={waveformRef} className="mb-4" />

          {/* Time display */}
          <div className="flex items-center justify-between text-sm font-mono">
            <span className="text-trail-tan">{formatTime(currentTime)}</span>
            <span className="text-trail-tan">{formatTime(duration)}</span>
          </div>
        </div>

        {/* Playback Controls */}
        <div className="terminal-border p-6 mb-6">
          <div className="flex items-center justify-center gap-4 mb-6">
            {/* Previous */}
            <button
              onClick={goToPrevious}
              disabled={!isReady}
              className="bg-gray-800 hover:bg-gray-700 text-white p-4 disabled:opacity-50
                       transition-colors border-2 border-sky-blue"
              title="Previous flashcard (←)"
            >
              <SkipBack size={24} />
            </button>

            {/* Play/Pause */}
            <button
              onClick={togglePlayPause}
              disabled={!isReady}
              className="bg-terminal-green hover:bg-green-400 text-black p-6 disabled:opacity-50
                       transition-colors font-bold"
              title="Play/Pause (Space)"
            >
              {isPlaying ? <Pause size={32} /> : <Play size={32} />}
            </button>

            {/* Next */}
            <button
              onClick={goToNext}
              disabled={!isReady}
              className="bg-gray-800 hover:bg-gray-700 text-white p-4 disabled:opacity-50
                       transition-colors border-2 border-sky-blue"
              title="Next flashcard (→)"
            >
              <SkipForward size={24} />
            </button>
          </div>

          {/* Secondary Controls */}
          <div className="flex items-center justify-center gap-4">
            {/* Play Region */}
            <button
              onClick={playRegion}
              disabled={!isReady || !currentFlashcard.audioClip}
              className="bg-sky-blue hover:bg-blue-400 text-black font-bold font-mono py-3 px-6
                       disabled:opacity-50 transition-colors"
              title="Play current segment (R)"
            >
              [PLAY SEGMENT]
            </button>

            {/* Loop Mode Toggle */}
            <button
              onClick={cycleLoopMode}
              disabled={!isReady}
              className={`font-bold font-mono py-3 px-6 disabled:opacity-50 transition-colors
                ${loopMode !== 'none' ? 'bg-hot-pink hover:bg-pink-400 text-white' : 'bg-gray-800 hover:bg-gray-700 text-trail-tan'}`}
              title={`Loop mode (L): ${loopMode.toUpperCase()}`}
            >
              {loopMode === 'none' && (
                <>
                  <X size={16} className="inline mr-2" />
                  [NO LOOP]
                </>
              )}
              {loopMode === 'single' && (
                <>
                  <Repeat1 size={16} className="inline mr-2" />
                  [LOOP SINGLE]
                </>
              )}
              {loopMode === 'all' && (
                <>
                  <Repeat size={16} className="inline mr-2" />
                  [LOOP ALL]
                </>
              )}
            </button>
          </div>
        </div>

        {/* Keyboard Shortcuts */}
        <div className="terminal-border p-6">
          <h3 className="text-lg font-bold text-terminal-green font-mono mb-4">
            [KEYBOARD SHORTCUTS]
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm font-mono">
            <div>
              <kbd className="bg-gray-800 px-2 py-1 text-sky-blue">Space</kbd>
              <p className="text-trail-tan mt-1">Play/Pause</p>
            </div>
            <div>
              <kbd className="bg-gray-800 px-2 py-1 text-sky-blue">←</kbd>
              <p className="text-trail-tan mt-1">Previous</p>
            </div>
            <div>
              <kbd className="bg-gray-800 px-2 py-1 text-sky-blue">→</kbd>
              <p className="text-trail-tan mt-1">Next</p>
            </div>
            <div>
              <kbd className="bg-gray-800 px-2 py-1 text-sky-blue">L</kbd>
              <p className="text-trail-tan mt-1">Toggle Loop</p>
            </div>
            <div>
              <kbd className="bg-gray-800 px-2 py-1 text-sky-blue">R</kbd>
              <p className="text-trail-tan mt-1">Restart Segment</p>
            </div>
          </div>
        </div>

        {/* Color Legend */}
        <div className="terminal-border p-6 mt-6">
          <h3 className="text-lg font-bold text-terminal-green font-mono mb-4">
            [CONFIDENCE COLORS]
          </h3>
          <div className="flex gap-6 text-sm font-mono">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-red-500 opacity-30"></div>
              <span className="text-trail-tan">&lt;70% - Needs Review</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-yellow-500 opacity-30"></div>
              <span className="text-trail-tan">70-85% - Verify</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-cyan-500 opacity-30"></div>
              <span className="text-trail-tan">&gt;85% - Good</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Audio Compliance: Initial Interaction Required */}
      {!hasUserInteraction && isReady && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="terminal-border p-8 max-w-md text-center">
            <h2 className="text-3xl font-bold text-terminal-green font-mono mb-4">
              [PRESS PLAY TO BEGIN]
            </h2>
            <p className="text-trail-tan font-mono text-sm mb-6">
              Mobile browsers require user interaction before playing audio.
              <br />
              Click the button below to enable automatic playback.
            </p>
            <button
              onClick={play}
              className="bg-terminal-green hover:bg-green-400 text-black font-bold font-mono py-4 px-8
                       transition-colors text-lg min-h-[60px]"
            >
              <Play size={24} className="inline mr-2" />
              [START AUDIO]
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
